import { MoneyValue, TermsIntervals } from '@ads-bread/shared/bread/codecs';
import Decimal from 'decimal.js-light';
import { toMoneyValue } from './money';
import { Duration } from './types';

/**
 * Parameters for loan calculations
 */
export interface LoanParameters {
  /** The principal amount of the loan in cents (MoneyValue) */
  principal: MoneyValue;
  /** The annual interest rate as a decimal (e.g., 0.0999 for 9.99%) */
  interestRate: number;
  /** The number of payment periods */
  termLength: number;
  /* The payment frequency - defaults to monthly if not specified */
  /* Either interval or duration must be specified to calculate payments
   * If both are passed, duration takes precedence
   */
  interval?: TermsIntervals;
  duration?: Duration;
}

/**
 * Represents a single payment in a calculated payment schedule
 */
export interface CalculatedPayment {
  paymentNumber: number;
  paymentAmount: MoneyValue;
  principalAmount: MoneyValue;
  interestAmount: MoneyValue;
  remainingBalance: MoneyValue;
}

/**
 * Complete calculated payment schedule for a loan
 */
export interface CalculatedPaymentSchedule {
  payments: CalculatedPayment[];
  totalInterest: MoneyValue;
  totalPayments: MoneyValue;
}

/**
 * Get the duration object for a given payment interval
 * @param interval - TermsIntervals (e.g., MONTHLY, BIWEEKLY)
 * @returns Duration
 */
function getDuration(interval?: TermsIntervals): Duration {
  switch (interval) {
    case 'BIWEEKLY':
      return { length: 4, designator: 'W' };
    default:
      return { length: 1, designator: 'M' };
  }
}

/**
 * Rounds interest rate to 6 decimal places to match backend precision
 * Backend: decimal.New(int64(math.Round(params.Rate*1e6)), -6)
 * This creates a decimal with exactly 6 decimal places
 * @param rate number
 * @returns number
 */
function roundRateToPrecision(rate: number): number {
  // Backend: decimal.New(int64(math.Round(params.Rate*1e6)), -6)
  // This rounds to 6 decimal places exactly
  return Math.round(rate * 1e6) / 1e6;
}

/**
 * Calculates the periodic interest rate based on payment frequency.
 *
 * @param interestRate - Annual interest rate as decimal (e.g., 0.05 for 5%)
 * @param duration - Payment frequency (MONTHLY or BIWEEKLY)
 * @returns Periodic interest rate as decimal
 *
 * Calculation logic:
 * - IL payments: annual rate ÷ 12
 * - SP payments: annual rate 0
 */
function calculatePeriodicRate(
  interestRate: number,
  duration: Duration
): number {
  return interestRate / (12 / duration.length);
}

/**
 * Converts rate in basis points (BPS) to decimal format
 * (e.g., 999 BPS -> 0.0999)
 * @param rateBPS number
 * @returns number
 */
export function rateBPStoRateDecimal(rateBPS: number): number {
  return rateBPS / 10000;
}

/**
 * Validates loan parameters
 * @param params LoanParameters
 */
export function validateLoanParameters(params: LoanParameters): void {
  if (params.principal < 0) {
    throw new Error('Principal amount must be positive');
  }
  if (params.interestRate < 0) {
    throw new Error('Interest rate cannot be negative');
  }
  if (params.termLength <= 0 || !Number.isInteger(params.termLength)) {
    throw new Error('Term length must be a positive integer');
  }
  if (!params.interval && !params.duration) {
    throw new Error('Payment interval or duration must be specified');
  }
}

/**
 * Calculates the standard monthly payment for a loan
 * Formula: PMT = r * PV * ((1 + r)^n) / (((1 + r)^n) - 1)
 * Uses the ROUND_DOWN strategy from accounting library to match backend behavior
 * @param params LoanParameters
 * @returns MoneyValue
 */
export function calculateStandardPayment(params: LoanParameters): MoneyValue {
  validateLoanParameters(params);

  const interestRate = roundRateToPrecision(params.interestRate);
  const principal = new Decimal(params.principal);
  const termLength = new Decimal(params.termLength);
  const duration: Duration = params.duration || getDuration(params.interval);

  // Check for unsupported bi-weekly + interest combination
  if (duration.designator === 'W' && params.interestRate > 0) {
    throw new Error(
      `Interest bearing loans with a BIWEEKLY frequency are unsupported. ${JSON.stringify(
        { params },
        null,
        '\t'
      )}`
    );
  }

  // Handle zero interest rate case: PMT = PV/n
  if (interestRate === 0) {
    const payment = principal.dividedBy(termLength);
    return toMoneyValue(Math.ceil(payment.toNumber()));
  }

  const periodicRateValue = calculatePeriodicRate(
    params.interestRate,
    duration
  );
  const periodicRate = new Decimal(periodicRateValue);

  // Calculate (1 + r)^n
  const onePlusRPowN = new Decimal(1).plus(periodicRate).pow(termLength);

  // PMT = r * PV * ((1 + r)^n) / (((1 + r)^n) - 1)
  const numerator = periodicRate.times(principal).times(onePlusRPowN);
  const denominator = onePlusRPowN.minus(1);
  const payment = numerator.dividedBy(denominator);

  return toMoneyValue(Math.ceil(payment.toNumber()));
}

/**
 * Calculates the final payment amount to ensure the loan is fully paid off
 * @param params LoanParameters
 * @returns MoneyValue
 */
export function calculateFinalPayment(params: LoanParameters): MoneyValue {
  validateLoanParameters(params);

  const schedule = generateCalculatedPaymentSchedule(params);
  const finalPayment = schedule.payments[schedule.payments.length - 1];

  if (!finalPayment) {
    throw new Error('No payments in schedule');
  }

  return finalPayment.paymentAmount;
}

/**
 * Calculates the total interest paid over the life of the loan
 * @param params LoanParameters
 * @returns MoneyValue
 */
export function calculateTotalInterest(params: LoanParameters): MoneyValue {
  validateLoanParameters(params);

  const schedule = generateCalculatedPaymentSchedule(params);
  return schedule.totalInterest;
}

/**
 * Generates a complete calculated payment schedule for the loan
 * @param params LoanParameters
 * @returns CalculatedPaymentSchedule
 */
export function generateCalculatedPaymentSchedule(
  params: LoanParameters
): CalculatedPaymentSchedule {
  validateLoanParameters(params);

  const { principal, termLength, interval } = params;
  const interestRate = roundRateToPrecision(params.interestRate);
  const duration: Duration = params.duration || getDuration(interval);
  const hasInterest = interestRate > 0;

  const standardPaymentDecimal = new Decimal(calculateStandardPayment(params));
  const periodicRate = new Decimal(
    calculatePeriodicRate(interestRate, duration)
  );
  const principalPerPayment = hasInterest
    ? null
    : new Decimal(principal).dividedBy(termLength);
  const zeroInterest = new Decimal(0);

  const payments: CalculatedPayment[] = [];
  let remainingBalance = new Decimal(principal);
  let totalInterest = new Decimal(0);

  for (let paymentNumber = 1; paymentNumber <= termLength; paymentNumber++) {
    const isLastPayment = paymentNumber === termLength;

    const { paymentAmount, interestAmount } = hasInterest
      ? calculateInterestBearingPayment(
          remainingBalance,
          standardPaymentDecimal,
          periodicRate,
          isLastPayment
        )
      : calculateZeroInterestPayment(
          principalPerPayment as Decimal,
          remainingBalance,
          zeroInterest,
          isLastPayment
        );

    const finalizedAmounts = applyRoundingRules(
      paymentAmount,
      interestAmount,
      remainingBalance,
      isLastPayment
    );

    // Update running totals
    remainingBalance = remainingBalance.minus(finalizedAmounts.principal);
    totalInterest = totalInterest.plus(finalizedAmounts.interest);

    payments.push({
      paymentNumber,
      paymentAmount: toMoneyValue(finalizedAmounts.payment),
      principalAmount: toMoneyValue(finalizedAmounts.principal),
      interestAmount: toMoneyValue(finalizedAmounts.interest),
      remainingBalance: toMoneyValue(
        Math.round(Math.max(0, remainingBalance.toNumber()))
      ),
    });
  }

  const totalPaymentAmount = payments.reduce(
    (sum, payment) => sum + payment.paymentAmount,
    0
  );

  return {
    payments,
    totalInterest: toMoneyValue(Math.round(totalInterest.toNumber())),
    totalPayments: toMoneyValue(Math.round(totalPaymentAmount)),
  };
}
interface CalculateInterestBearingPaymentResult {
  paymentAmount: Decimal;
  interestAmount: Decimal;
  principalAmount: Decimal;
}

/**
 * Calculates payment components for interest-bearing loans
 * @param remainingBalance Decimal - Current remaining balance on the loan
 * @param standardPayment Decimal - Standard payment amount calculated for the loan
 * @param periodicRate Decimal - Periodic interest rate as decimal
 * @param isLastPayment boolean - Whether this is the final payment in the schedule
 * @returns CalculateInterestBearingPaymentResult
 */
function calculateInterestBearingPayment(
  remainingBalance: Decimal,
  standardPayment: Decimal,
  periodicRate: Decimal,
  isLastPayment: boolean
): CalculateInterestBearingPaymentResult {
  const interestAmount = remainingBalance.times(periodicRate);

  if (isLastPayment) {
    // Final payment: pay off exact remaining balance
    const principalAmount = remainingBalance;
    const paymentAmount = principalAmount.plus(interestAmount);
    return { paymentAmount, interestAmount, principalAmount };
  } else {
    // Regular payment: use standard payment amount with ceiling rounding
    const paymentAmount = new Decimal(Math.ceil(standardPayment.toNumber()));
    const principalAmount = paymentAmount.minus(interestAmount);
    return { paymentAmount, interestAmount, principalAmount };
  }
}

interface CalculateZeroInterestPaymentResult {
  paymentAmount: Decimal;
  interestAmount: Decimal;
  principalAmount: Decimal;
}

/**
 * Calculates payment components for zero-interest loans
 * @param principalPerPayment Decimal - Pre-calculated principal per payment
 * (principal ÷ termLength)
 * @param remainingBalance Decimal - Current remaining balance on the loan
 * @param zeroInterest Decimal - Reusable Decimal(0) object for the interest amount
 * @param isLastPayment boolean - Whether this is the final payment in the schedule
 * @returns CalculateZeroInterestPaymentResult
 */
function calculateZeroInterestPayment(
  principalPerPayment: Decimal,
  remainingBalance: Decimal,
  zeroInterest: Decimal,
  isLastPayment: boolean
): CalculateZeroInterestPaymentResult {
  if (isLastPayment) {
    // Final payment: pay exact remaining balance
    const principalAmount = remainingBalance;
    const paymentAmount = new Decimal(Math.ceil(principalAmount.toNumber()));
    return { paymentAmount, interestAmount: zeroInterest, principalAmount };
  } else {
    // Regular payment: equal principal per payment with ceiling rounding
    const paymentAmount = new Decimal(
      Math.ceil(principalPerPayment.toNumber())
    );
    return {
      paymentAmount,
      interestAmount: zeroInterest,
      principalAmount: principalPerPayment,
    };
  }
}

interface ApplyRoundingRulesResult {
  payment: number;
  interest: number;
  principal: number;
}
/**
 * Applies final rounding rules to convert Decimal amounts to cents
 * @param paymentAmount Decimal - Calculated payment amount before final rounding
 * @param interestAmount Decimal - Interest portion of the payment
 * @param remainingBalance Decimal - Current remaining balance (used for final payment)
 * @param isLastPayment boolean - Whether this is the final payment in the schedule
 * @returns Object with finalized payment, interest, and principal amounts in cents
 */
function applyRoundingRules(
  paymentAmount: Decimal,
  interestAmount: Decimal,
  remainingBalance: Decimal,
  isLastPayment: boolean
): ApplyRoundingRulesResult {
  if (isLastPayment) {
    // Final payment: exact interest and principal (no ceiling rounding)
    const interest = Math.floor(interestAmount.toNumber());
    const principal = remainingBalance.toNumber();
    const payment = principal + interest;
    return { payment, interest, principal };
  } else {
    // Regular payment: ceiling rounded payment, floored interest
    const payment = Math.ceil(paymentAmount.toNumber());
    const interest = Math.floor(interestAmount.toNumber());
    const principal = payment - interest;
    return { payment, interest, principal };
  }
}
